import type { Route } from 'vue-router'

import {
  extractPathComponents,
  makePathFromComponents,
  nonProviderRoutes,
} from '../'

function getBeforeEachRouteGuards(router) {
  const { features } = useFeatures()
  const { currentProviderPrefix, currentCustomerId, isAuthenticated, hasUnagreedPolicies, unagreedPolicies } = useSessionData()

  function redirectTo(route, useRouter = true) {
    if (useRouter)
      router.push(route)
    else window.location.href = route
    return false
  }

  const consoleLogToPathElements = (to) => {
    console.log('router.beforeEach', to, extractPathComponents(to.path))
  }

  const providerFeatureFlagRouteGuard = (to) => {
    const { path, providerPrefix, widgetPrefix } = extractPathComponents(to.path)
    if (!features.customer_v2 && !nonProviderRoutes.includes(path)) {
      window.location.href = makePathFromComponents({
        path,
        providerPrefix,
        widgetPrefix,
      }, {
        includeBaseRoute: false,
      })
      return false
    }
  }

  const catchAllRouteGuard = (to) => {
    if (to.matched.length === 0) {
      const { path, providerPrefix, widgetPrefix } = extractPathComponents(to.path)

      const dashboardPath = makePathFromComponents({
        path: '/dashboard',
        providerPrefix,
        widgetPrefix,
      })

      const errorPath = makePathFromComponents({
        path: '/404',
        widgetPrefix,
      })

      const defaultPath = ''
      if (path !== defaultPath)
        return redirectTo(errorPath)

      const { isWidget, widgetSettingsOptionsType, widgetSettingsOptions, widgetSettingsOptionsView } = useWidgetSettings()
      const { getFeatureRouteByKey, firstVisibleFeatureRoute } = useFeatureRoutes()

      if (!providerPrefix)
        return redirectTo(dashboardPath)

      if (isWidget) {
        if (widgetSettingsOptionsType === 'freetrial') {
          const freeTrialPath = makePathFromComponents({
            path: `/start/?membership_id=${widgetSettingsOptions.free_trial_membership}`,
            providerPrefix,
            widgetPrefix,
          }, {
            includeBaseRoute: false,
          })
          return redirectTo(freeTrialPath, false)
        }
        else if (widgetSettingsOptionsView) {
          const viewFeatureRoute = getFeatureRouteByKey(widgetSettingsOptionsView)
          if (viewFeatureRoute) {
            const viewFeatureRoutePath = makePathFromComponents({
              path: viewFeatureRoute.path,
              providerPrefix,
              widgetPrefix,
            }, {
              includeBaseRoute: viewFeatureRoute.v2Path,
            })
            return redirectTo(viewFeatureRoutePath, viewFeatureRoute.v2Path)
          }
        }
      }

      if (firstVisibleFeatureRoute) {
        const firstVisibleFeatureRoutePath = makePathFromComponents({
          path: firstVisibleFeatureRoute.path,
          providerPrefix,
          widgetPrefix,
        }, {
          includeBaseRoute: firstVisibleFeatureRoute.v2Path,
        })
        return redirectTo(firstVisibleFeatureRoutePath, firstVisibleFeatureRoute.v2Path)
      }

      return redirectTo(dashboardPath)
    }
  }

  const authenticationRequiredRouteGuard = (to: Route) => {
    if (to.meta?.authentication_required && !isAuthenticated) {
      const pathComponents = extractPathComponents(to.path)
      window.location.href = makePathFromComponents({
        ...pathComponents,
        path: `/login?next=${to.fullPath}`,
      }, {
        includeBaseRoute: false,
        includeProviderPrefix: false,
      })
      return false
    }
  }

  const customerRequiredRouteGuard = (to: Route) => {
    if (to.meta?.customer_required && !currentCustomerId) {
      const pathComponents = extractPathComponents(to.path)
      window.location.href = makePathFromComponents({
        ...pathComponents,
        path: `/start?next=${to.fullPath}`,
        providerPrefix: currentProviderPrefix,
      }, {
        includeBaseRoute: false,
      })
      return false
    }
  }

  const unagreedPolicyRouteGuard = (to: Route) => {
    if (hasUnagreedPolicies) {
      const { id } = unagreedPolicies[0]
      const pathComponents = extractPathComponents(to.path)
      const path = `/account/policies/${id}`
      if (pathComponents.path !== path) {
        return redirectTo(
          makePathFromComponents({
            ...pathComponents,
            path,
            providerPrefix: currentProviderPrefix,
          }),
          true,
        )
      }
    }
  }

  const routeVisibilityRouteGuard = (to) => {
    const pathComponents = extractPathComponents(to.path)
    if (pathComponents.path === '/dashboard')
      return
    const { getFeatureRouteByPath, getClosestVisibleFeatureRouteByPath } = useFeatureRoutes()
    const route = getFeatureRouteByPath(pathComponents.path)

    if (route?.visible !== false)
      return
    const closestVisibleRoute = getClosestVisibleFeatureRouteByPath(pathComponents.path)
    const closestVisibleRoutePath = makePathFromComponents({
      ...pathComponents,
      path: closestVisibleRoute.path,
      providerPrefix: currentProviderPrefix,
    }, {
      includeBaseRoute: closestVisibleRoute.v2Path,
    })
    return redirectTo(closestVisibleRoutePath, closestVisibleRoute.v2Path)
  }

  return [
    // consoleLogToPathElements,
    catchAllRouteGuard,
    providerFeatureFlagRouteGuard,
    authenticationRequiredRouteGuard,
    customerRequiredRouteGuard,
    unagreedPolicyRouteGuard,
    routeVisibilityRouteGuard,
  ]
}
export default getBeforeEachRouteGuards
